import { useState, useEffect, useMemo } from "react";
import {
    Loader2,
    AlertCircle,
    Search,
    SlidersHorizontal,
    X,
    Calendar,
} from "lucide-react";
import {
    CallSessionTable,
    SortField,
} from "@component/agent-calls/call-session-table";
import { Pagination } from "@component/pagination";
import { CALL_CATEGORIES } from "@type/agent";
import type {
    CallSession,
    CampaignListItem,
} from "@type/agent";
import {
    getAgentCallSessions,
    getAgentCampaigns,
} from "@service/agent";
import { cn } from "@util/tailwind";

const PAGE_LIMIT = 20;

const callStatusFilters = [
    { value: "all", label: "All" },
    { value: "results_ready", label: "Completed" },
    { value: "failed", label: "Failed" },
    { value: "active", label: "In Progress" },
    { value: "analyzing", label: "Analyzing" },
    { value: "pending", label: "Pending" },
];

const sentimentFilterOptions = [
    { value: "all", label: "All Sentiment" },
    { value: "positive", label: "Positive" },
    { value: "neutral", label: "Neutral" },
    { value: "negative", label: "Negative" },
    { value: "mixed", label: "Mixed" },
];

const categoryFilterOptions = [
    { value: "all", label: "All Categories" },
    ...Object.entries(CALL_CATEGORIES).map(([value, config]) => ({
        value,
        label: config.label,
    })),
];

function FilterSection({
    title,
    options,
    value,
    onChange,
}: {
    title: string;
    options: { value: string; label: string }[];
    value: string;
    onChange: (value: string) => void;
}) {
    return (
        <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                {title}
            </p>
            <div className="flex flex-wrap gap-2">
                {options.map((option) => (
                    <button
                        key={option.value}
                        onClick={() => onChange(option.value)}
                        className={cn(
                            "px-3 py-1.5 text-sm rounded-full border transition-colors",
                            value === option.value
                                ? "bg-blue-50 border-blue-600 text-blue-600 font-medium"
                                : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300"
                        )}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

function DateFilterInput({
    label,
    value,
    onChange,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
}) {
    return (
        <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                {label}
            </p>
            <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                    type="date"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent dark:bg-gray-900 dark:border-gray-700 dark:text-white w-44"
                />
            </div>
        </div>
    );
}

export default function AgentCalls() {
    const [statusFilter, setStatusFilter] = useState("all");
    const [campaignFilter, setCampaignFilter] = useState("all");
    const [sentimentFilter, setSentimentFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [dateAfter, setDateAfter] = useState("");
    const [dateBefore, setDateBefore] = useState("");

    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    const [sessions, setSessions] = useState<CallSession[]>([]);
    const [campaigns, setCampaigns] = useState<CampaignListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [sortField, setSortField] = useState<SortField>("timestamp");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, campaignFilter, sentimentFilter, categoryFilter, dateAfter, dateBefore, debouncedSearch]);

    // Load initial data
    useEffect(() => {
        async function loadInitial() {
            try {
                const campaignsList = await getAgentCampaigns();
                setCampaigns(campaignsList);
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : "Failed to load data"
                );
            } finally {
                setLoading(false);
            }
        }
        loadInitial();
    }, []);

    // Fetch sessions when filters or page change
    useEffect(() => {
        async function fetchSessions() {
            setSessionsLoading(true);
            try {
                const filters: Record<string, string | number | undefined> = {
                    page: currentPage,
                    limit: PAGE_LIMIT,
                };
                if (debouncedSearch) filters.search = debouncedSearch;
                if (statusFilter !== "all") filters.status = statusFilter;
                if (campaignFilter !== "all")
                    filters.campaignId = campaignFilter;
                if (sentimentFilter !== "all")
                    filters.sentiment = sentimentFilter;
                if (categoryFilter !== "all")
                    filters.category = categoryFilter;
                if (dateAfter) filters.dateAfter = dateAfter;
                if (dateBefore) filters.dateBefore = dateBefore;
                const result = await getAgentCallSessions(filters);
                setSessions(result.sessions);
                setTotalCount(result.total);
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : "Failed to load sessions"
                );
            } finally {
                setSessionsLoading(false);
            }
        }
        fetchSessions();
    }, [
        statusFilter,
        campaignFilter,
        sentimentFilter,
        categoryFilter,
        dateAfter,
        dateBefore,
        debouncedSearch,
        currentPage,
    ]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortDirection("desc");
        }
    };

    const sortedSessions = useMemo(() => {
        return [...sessions].sort((a, b) => {
            let cmp = 0;
            switch (sortField) {
                case "timestamp":
                    cmp =
                        new Date(a.timestamp).getTime() -
                        new Date(b.timestamp).getTime();
                    break;
                case "contactName":
                    cmp = (a.contactName || "").localeCompare(b.contactName || "");
                    break;
                case "duration":
                    cmp = a.duration - b.duration;
                    break;
                case "sentiment":
                    cmp = (a.sentiment || "").localeCompare(b.sentiment || "");
                    break;
                case "score":
                    cmp = a.score - b.score;
                    break;
                case "status":
                    cmp = a.status.localeCompare(b.status);
                    break;
            }
            return sortDirection === "asc" ? cmp : -cmp;
        });
    }, [sessions, sortField, sortDirection]);

    const campaignFilterOptions = [
        { value: "all", label: "All Campaigns" },
        ...campaigns.map((c) => ({ value: c.id, label: c.name })),
    ];

    const totalPages = Math.ceil(totalCount / PAGE_LIMIT);

    const hasAdvancedFilters =
        sentimentFilter !== "all" ||
        categoryFilter !== "all" ||
        campaignFilter !== "all" ||
        dateAfter !== "" ||
        dateBefore !== "";

    const advancedFilterCount = [
        sentimentFilter !== "all",
        categoryFilter !== "all",
        campaignFilter !== "all",
        dateAfter !== "",
        dateBefore !== "",
    ].filter(Boolean).length;

    const handleClearAdvancedFilters = () => {
        setSentimentFilter("all");
        setCategoryFilter("all");
        setCampaignFilter("all");
        setDateAfter("");
        setDateBefore("");
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
                <AlertCircle className="w-5 h-5" />
                <span>{error}</span>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    Call Log
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    View and manage all call sessions.
                </p>
            </div>

            {/* Status tabs + search row */}
            <div className="flex items-center justify-between gap-4">
                {/* Status filter tabs */}
                <div className="flex items-center gap-1.5">
                    {callStatusFilters.map((filter) => (
                        <button
                            key={filter.value}
                            onClick={() => setStatusFilter(filter.value)}
                            className={cn(
                                "inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors",
                                statusFilter === filter.value
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "text-gray-500 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                            )}
                        >
                            {filter.label}
                            {statusFilter === filter.value && totalCount > 0 && (
                                <span className={cn(
                                    "text-xs font-medium",
                                    statusFilter === filter.value ? "opacity-70" : "opacity-50"
                                )}>
                                    {totalCount}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Search + Filters button */}
                <div className="flex items-center gap-2">
                    {/* Search bar */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search calls..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 pr-4 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg w-64 bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent placeholder:text-gray-400"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    {/* Filters toggle button */}
                    <button
                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                        className={cn(
                            "inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-lg border transition-colors",
                            showAdvancedFilters || hasAdvancedFilters
                                ? "bg-blue-50 border-blue-200 text-blue-600"
                                : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 dark:bg-gray-900 dark:border-gray-700"
                        )}
                    >
                        <SlidersHorizontal className="w-4 h-4" />
                        Filters
                        {advancedFilterCount > 0 && (
                            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full bg-blue-600 text-white">
                                {advancedFilterCount}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* Collapsible advanced filters panel */}
            {showAdvancedFilters && (
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 space-y-5">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            Advanced Filters
                        </h3>
                        {hasAdvancedFilters && (
                            <button
                                onClick={handleClearAdvancedFilters}
                                className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                            >
                                Clear all
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        <FilterSection
                            title="Sentiment"
                            options={sentimentFilterOptions}
                            value={sentimentFilter}
                            onChange={setSentimentFilter}
                        />
                        <FilterSection
                            title="Category"
                            options={categoryFilterOptions}
                            value={categoryFilter}
                            onChange={setCategoryFilter}
                        />
                        <FilterSection
                            title="Campaign"
                            options={campaignFilterOptions}
                            value={campaignFilter}
                            onChange={setCampaignFilter}
                        />
                    </div>

                    <div className="flex items-end gap-4 pt-2 border-t border-gray-100 dark:border-gray-800">
                        <DateFilterInput
                            label="Date After"
                            value={dateAfter}
                            onChange={setDateAfter}
                        />
                        <DateFilterInput
                            label="Date Before"
                            value={dateBefore}
                            onChange={setDateBefore}
                        />
                        {(dateAfter || dateBefore) && (
                            <button
                                onClick={() => { setDateAfter(""); setDateBefore(""); }}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
                            >
                                <X className="w-3 h-3" />
                                Clear dates
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Active filter chips (shown when advanced panel is closed but filters are active) */}
            {!showAdvancedFilters && hasAdvancedFilters && (
                <div className="flex flex-wrap items-center gap-2">
                    {sentimentFilter !== "all" && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full bg-blue-50 border border-blue-200 text-blue-600">
                            Sentiment: {sentimentFilterOptions.find(o => o.value === sentimentFilter)?.label}
                            <button onClick={() => setSentimentFilter("all")} className="hover:text-blue-800">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    )}
                    {categoryFilter !== "all" && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full bg-blue-50 border border-blue-200 text-blue-600">
                            Category: {categoryFilterOptions.find(o => o.value === categoryFilter)?.label}
                            <button onClick={() => setCategoryFilter("all")} className="hover:text-blue-800">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    )}
                    {campaignFilter !== "all" && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full bg-blue-50 border border-blue-200 text-blue-600">
                            Campaign: {campaignFilterOptions.find(o => o.value === campaignFilter)?.label}
                            <button onClick={() => setCampaignFilter("all")} className="hover:text-blue-800">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    )}
                    {dateAfter && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full bg-blue-50 border border-blue-200 text-blue-600">
                            After: {dateAfter}
                            <button onClick={() => setDateAfter("")} className="hover:text-blue-800">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    )}
                    {dateBefore && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full bg-blue-50 border border-blue-200 text-blue-600">
                            Before: {dateBefore}
                            <button onClick={() => setDateBefore("")} className="hover:text-blue-800">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    )}
                    <button
                        onClick={handleClearAdvancedFilters}
                        className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        Clear all
                    </button>
                </div>
            )}

            {/* Results count */}
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {totalCount} {totalCount === 1 ? "call" : "calls"} found
                </p>
            </div>

            {/* Table */}
            <CallSessionTable
                sessions={sortedSessions}
                loading={sessionsLoading}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
            />

            {/* Pagination */}
            <Pagination
                isLoading={sessionsLoading}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
            />
        </div>
    );
}
